import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { differenceInSeconds } from 'date-fns';

// Intentionally ordered by time duration (smallest to largest), not alphabetically
/* eslint-disable sort-keys */
const unitMap = {
  second: 1,
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800,
  month: 2629746,
  year: 31536000,
};
/* eslint-enable sort-keys */

const getUnit = (secondsDelta: number): keyof typeof unitMap => {
  const absSeconds = Math.abs(secondsDelta);
  return (Object.keys(unitMap) as (keyof typeof unitMap)[])
    .reverse()
    .find(key => absSeconds >= unitMap[key]) ?? 'second';
};

/**
 * Hook that returns a function to format timestamps as relative time (e.g., "2 hours ago", "yesterday").
 * Uses the native Intl.RelativeTimeFormat API for proper localization.
 *
 * @returns A memoized function that accepts a timestamp and optional formatting options
 *
 * @example
 * const formatTimestamp = useRelativeFormatTimeStamp();
 *
 * // In render or loops:
 * return <div>{formatTimestamp(user.lastLoginAt)}</div>;
 *
 * // With custom options:
 * return <div>{formatTimestamp(user.lastLoginAt, { numeric: 'always' })}</div>;
 */
export const useRelativeFormatTimeStamp = () => {
  const intl = useIntl();

  return useCallback((timestamp: string | number | Date | null | undefined, options?: Intl.RelativeTimeFormatOptions) => {
    if (!timestamp) return undefined;

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return undefined;

    const mergedOptions: Intl.RelativeTimeFormatOptions = { numeric: 'auto', ...options };
    const secondsDelta = differenceInSeconds(date, new Date());
    const unit = getUnit(secondsDelta);
    const value = Math.round(secondsDelta / unitMap[unit]);

    return unit === 'second' ?
      intl.formatRelativeTime(0, unit, mergedOptions) :
      intl.formatRelativeTime(value, unit, mergedOptions);
  }, [intl]);
};
